import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { Download, TrendingUp, ShoppingBag, Receipt, Calendar, Clock, IceCream } from 'lucide-react';
import { useSalesReport, useTopProducts, useTopFlavors, useHourlyReport, usePreparationTimeReport } from '../../hooks/useReports';
import { downloadCsvExport } from '../../services/ReportsService';

// ── Helpers ────────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function formatCOP(value: number) {
  return '$ ' + Math.round(value).toLocaleString('es-CO');
}

// ── KPI Card ───────────────────────────────────────────────────────────────────

interface KpiProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  loading?: boolean;
}

function KpiCard({ label, value, icon, loading }: KpiProps) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-card">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</p>
        {loading ? (
          <div className="mt-1 h-6 w-32 animate-pulse rounded bg-muted" />
        ) : (
          <p className="truncate text-xl font-bold text-text">{value}</p>
        )}
      </div>
    </div>
  );
}

// ── Custom tooltip for bar chart ───────────────────────────────────────────────

function RevenueTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-3 shadow-card text-sm">
      <p className="font-semibold text-text mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {p.dataKey === 'totalRevenue' || p.dataKey === 'revenue' ? formatCOP(p.value) : p.value}
        </p>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function Reports() {
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(today());
  const [hourlyDate, setHourlyDate] = useState(today());
  const [exporting, setExporting] = useState(false);

  const sales = useSalesReport(from, to);
  const topProducts = useTopProducts(from, to, 10);
  const topFlavors = useTopFlavors(from, to, 10);
  const hourly = useHourlyReport(hourlyDate);
  const prepTime = usePreparationTimeReport(from, to);

  async function handleExport() {
    setExporting(true);
    try {
      await downloadCsvExport(from, to);
    } finally {
      setExporting(false);
    }
  }

  const dailyData = (sales.data?.daily ?? []).map(d => ({
    ...d,
    label: d.date.slice(5), // MM-DD
  }));

  // Only show hours with at least 1 order for top-products style filtering, but keep all for chart
  const hourlyData = hourly.data?.hours ?? [];

  return (
    <div className="min-h-screen bg-bg p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-8">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Reportes de Ventas</h1>
            <p className="text-sm text-text-muted">Análisis de ventas e ingresos</p>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || sales.isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow transition hover:opacity-90 disabled:opacity-50"
          >
            <Download size={16} />
            {exporting ? 'Descargando…' : 'Exportar CSV'}
          </button>
        </div>

        {/* Date range filter */}
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-surface px-5 py-4">
          <Calendar size={18} className="text-text-muted" />
          <label className="text-sm font-medium text-text-muted">Desde</label>
          <input
            type="date"
            value={from}
            max={to}
            onChange={e => setFrom(e.target.value)}
            className="rounded-lg border border-border bg-bg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <label className="text-sm font-medium text-text-muted">Hasta</label>
          <input
            type="date"
            value={to}
            min={from}
            max={today()}
            onChange={e => setTo(e.target.value)}
            className="rounded-lg border border-border bg-bg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {/* Quick presets */}
          <div className="ml-auto flex gap-2">
            {[
              { label: 'Hoy', from: today(), to: today() },
              { label: '7d', from: daysAgo(6), to: today() },
              { label: '30d', from: daysAgo(29), to: today() },
            ].map(p => (
              <button
                key={p.label}
                onClick={() => { setFrom(p.from); setTo(p.to); }}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  from === p.from && to === p.to
                    ? 'bg-primary text-white'
                    : 'bg-muted text-text-muted hover:bg-primary/10'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiCard
            label="Ingresos totales"
            value={formatCOP(sales.data?.totalRevenue ?? 0)}
            icon={<TrendingUp size={22} />}
            loading={sales.isLoading}
          />
          <KpiCard
            label="Pedidos cobrados"
            value={String(sales.data?.totalOrders ?? 0)}
            icon={<ShoppingBag size={22} />}
            loading={sales.isLoading}
          />
          <KpiCard
            label="Ticket promedio"
            value={formatCOP(sales.data?.avgTicket ?? 0)}
            icon={<Receipt size={22} />}
            loading={sales.isLoading}
          />
        </div>

        {/* Daily revenue chart */}
        <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
          <h2 className="mb-4 text-base font-semibold text-text">Ingresos por día</h2>
          {sales.isLoading ? (
            <div className="flex h-56 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : dailyData.length === 0 ? (
            <div className="flex h-56 items-center justify-center text-sm text-text-muted">
              Sin ventas en el período seleccionado
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={dailyData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e5e7eb)" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) => '$ ' + (v / 1000).toFixed(0) + 'k'}
                  width={60}
                />
                <Tooltip content={<RevenueTooltip />} />
                <Bar dataKey="totalRevenue" name="Ingresos" fill="#6366f1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Hourly chart */}
        <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-text">Ventas por hora</h2>
            <input
              type="date"
              value={hourlyDate}
              max={today()}
              onChange={e => setHourlyDate(e.target.value)}
              className="rounded-lg border border-border bg-bg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          {hourly.isLoading ? (
            <div className="flex h-56 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={hourlyData}
                margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e5e7eb)" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  interval={2}
                />
                <YAxis tick={{ fontSize: 11 }} width={32} />
                <Tooltip content={<RevenueTooltip />} />
                <Bar
                  dataKey="totalOrders"
                  name="Pedidos"
                  fill="#22c55e"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top products + Top flavors */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

          {/* Top products */}
          <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-text">Top productos</h2>
            {topProducts.isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-4 w-4 shrink-0 animate-pulse rounded bg-muted" />
                    <div className="h-4 flex-1 animate-pulse rounded bg-muted" />
                    <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                  </div>
                ))}
              </div>
            ) : (topProducts.data?.products ?? []).length === 0 ? (
              <p className="text-sm text-text-muted">Sin datos en el período</p>
            ) : (
              <div className="space-y-2">
                {(topProducts.data?.products ?? []).map((p, idx) => {
                  const maxQty = topProducts.data!.products[0]?.totalQty ?? 1;
                  const pct = Math.round((p.totalQty / maxQty) * 100);
                  return (
                    <div key={p.id} className="flex items-center gap-3">
                      <span className="w-5 shrink-0 text-xs font-bold text-text-muted">
                        {idx + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-medium text-text">{p.name}</p>
                          <p className="shrink-0 text-xs text-text-muted">{formatCOP(p.totalRevenue)}</p>
                        </div>
                        <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
                          <div
                            className="h-1.5 rounded-full bg-primary"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="mt-0.5 text-xs text-text-muted">{p.totalQty} unid.</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top flavors */}
          <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
            <div className="mb-4 flex items-center gap-2">
              <IceCream size={18} className="text-primary" />
              <h2 className="text-base font-semibold text-text">Sabores de helado más vendidos</h2>
            </div>
            {topFlavors.isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-4 w-4 shrink-0 animate-pulse rounded bg-muted" />
                    <div className="h-4 flex-1 animate-pulse rounded bg-muted" />
                    <div className="h-4 w-12 animate-pulse rounded bg-muted" />
                  </div>
                ))}
              </div>
            ) : (topFlavors.data?.flavors ?? []).length === 0 ? (
              <p className="text-sm text-text-muted">Sin datos en el período</p>
            ) : (
              <div className="space-y-2">
                {(topFlavors.data?.flavors ?? []).map((f, idx) => {
                  const maxQty = topFlavors.data!.flavors[0]?.totalQty ?? 1;
                  const pct = Math.round((f.totalQty / maxQty) * 100);
                  return (
                    <div key={f.id} className="flex items-center gap-3">
                      <span className="w-5 shrink-0 text-xs font-bold text-text-muted">
                        {idx + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-medium text-text">{f.name}</p>
                          <p className="shrink-0 text-xs font-semibold text-primary">{f.totalQty} bolas/porciones</p>
                        </div>
                        <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
                          <div
                            className="h-1.5 rounded-full bg-primary"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Preparation time per area */}
        <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
          <div className="mb-5 flex items-center gap-2">
            <Clock size={18} className="text-primary" />
            <h2 className="text-base font-semibold text-text">Tiempo promedio de preparación por área</h2>
          </div>

          {prepTime.isLoading ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="h-16 animate-pulse rounded-xl bg-muted" />
              ))}
            </div>
          ) : (prepTime.data?.areas ?? []).length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-text-muted">
              <Clock size={32} className="opacity-30" />
              <p>Sin datos para el período seleccionado.</p>
              <p className="text-xs">Los tiempos se registran cuando la cocina mueve órdenes de <strong>En preparación</strong> a <strong>Listo</strong>.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(prepTime.data?.areas ?? []).map(area => (
                <div
                  key={area.area}
                  className="rounded-xl border border-border bg-bg p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-text">{area.areaLabel}</span>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {area.totalOrders} órdenes
                    </span>
                  </div>

                  <div className="text-center py-2">
                    <p className="text-3xl font-bold text-text">{area.avgMinutes}<span className="text-base font-normal text-text-muted ml-1">min</span></p>
                    <p className="text-xs text-text-muted mt-1">promedio</p>
                  </div>

                  <div className="flex justify-between text-xs text-text-muted border-t border-border pt-3">
                    <div className="text-center">
                      <p className="font-semibold text-emerald-600">{area.minMinutes} min</p>
                      <p>mínimo</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-red-500">{area.maxMinutes} min</p>
                      <p>máximo</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error states */}
        {(sales.isError || topProducts.isError || hourly.isError) && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4 text-sm text-amber-800">
            Error al cargar algunos datos. Verifica tu conexión e intenta de nuevo.
          </div>
        )}

      </div>
    </div>
  );
}
